import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

const computerTypes = [
  { icon: 'ri-computer-line', label: 'Desktop Computers', desc: 'PC towers, iMacs, all-in-ones from any brand or era' },
  { icon: 'ri-macbook-line', label: 'Laptops & MacBooks', desc: 'Windows laptops, MacBooks, Chromebooks, and ultrabooks' },
  { icon: 'ri-server-line', label: 'Servers & Workstations', desc: 'Rack servers, blade servers, NAS devices, and workstations' },
  { icon: 'ri-keyboard-line', label: 'Monitors & Screens', desc: 'CRT monitors, LCD, LED, and all desktop display types' },
  { icon: 'ri-printer-line', label: 'Printers & Scanners', desc: 'Inkjet, laser, copiers, fax machines, and multifunction devices' },
  { icon: 'ri-router-line', label: 'Networking Equipment', desc: 'Routers, switches, hubs, modems, and network gear' },
];

const benefits = [
  { icon: 'ri-shield-check-line', title: 'Free Data Destruction', desc: 'Every computer recycled with us gets free data wiping to DoD standards. No extra charge.' },
  { icon: 'ri-award-line', title: 'CalRecycle Certified', desc: 'We\'re California\'s certified e-waste recycler CEWD-127531 — legally compliant disposal guaranteed.' },
  { icon: 'ri-truck-line', title: 'Free Business Pickup', desc: 'Upgrading your office? We pick up all your old computers for free anywhere in LA County.' },
  { icon: 'ri-file-list-3-line', title: 'Certificate Provided', desc: 'Receive a certificate of recycling for your records, audit compliance, and peace of mind.' },
];

const faqs = [
  { q: 'Is computer recycling free in Los Angeles?', a: 'Yes! Drop-off computer recycling is free at both our Los Angeles locations: 2542 W. 3rd Street (90057) and 4902 W. Slauson Ave (90056). Free pickup is available for qualifying businesses throughout LA County.' },
  { q: 'Do you wipe the data before recycling computers?', a: 'Yes — every computer we recycle gets free data wiping using DoD 5220.22-M multi-pass overwrite standards. For maximum security, we also offer physical hard drive shredding with a Certificate of Destruction.' },
  { q: 'What brands of computers do you accept?', a: 'All brands — Dell, HP, Apple, Lenovo, Acer, Asus, Microsoft Surface, Toshiba, Samsung, Gateway, IBM, and any other manufacturer. Age and condition don\'t matter.' },
  { q: 'Can I recycle a broken or non-working computer?', a: 'Absolutely. Broken, cracked, water-damaged, or completely non-functional computers are accepted. We recycle the components responsibly regardless of condition.' },
  { q: 'Do you accept old CRT monitors with computers?', a: 'Yes. CRT monitors are accepted at both our LA drop-off locations. These contain lead and must be recycled properly — we handle them safely and in compliance with California law.' },
];

const ComputerRecyclingPage = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://greenlyelectronicsrecycling.com';

  return (
    <>
      <title>Computer Recycling Los Angeles | Free PC &amp; Laptop Recycling | Greenly</title>
      <meta name="description" content="Free computer recycling in Los Angeles — desktops, laptops, Macs, servers & monitors accepted. Free data destruction included. 2 drop-off locations. CalRecycle CEWD-127531. Call (213) 600-1000." />
      <meta name="keywords" content="computer recycling Los Angeles, laptop recycling LA, PC recycling Los Angeles, free computer recycling LA, Mac recycling Los Angeles" />
      <meta name="robots" content="index, follow" />
      <meta name="last-modified" content="2026-05-11" />
      <link rel="canonical" href={`${SITE_URL}/computer-recycling`} />
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "WebPage",
            "@id": `${SITE_URL}/computer-recycling#webpage`,
            "url": `${SITE_URL}/computer-recycling`,
            "name": "Computer Recycling Los Angeles | Free PC & Laptop Recycling | Greenly",
            "description": "Free computer recycling in Los Angeles. We accept desktops, laptops, Macs, servers & monitors. Free data destruction included. 2 drop-off locations. CalRecycle CEWD-127531. Call (213) 600-1000.",
            "isPartOf": { "@id": `${SITE_URL}/#website` },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
                { "@type": "ListItem", "position": 2, "name": "Computer Recycling Los Angeles", "item": `${SITE_URL}/computer-recycling` }
              ]
            }
          },
          {
            "@type": "Service",
            "@id": `${SITE_URL}/computer-recycling#service`,
            "name": "Computer Recycling Los Angeles",
            "description": "Free computer recycling in Los Angeles. We accept desktops, laptops, Macs, servers and monitors. Free data destruction included. Two drop-off locations. CalRecycle CEWD-127531.",
            "url": `${SITE_URL}/computer-recycling`,
            "provider": { "@id": `${SITE_URL}/#business` },
            "areaServed": { "@type": "City", "name": "Los Angeles" },
            "serviceType": "Electronics Recycling",
            "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD", "description": "Free computer recycling drop-off with free data destruction" }
          },
          {
            "@type": "FAQPage",
            "@id": `${SITE_URL}/computer-recycling#faq`,
            "mainEntity": faqs.map((faq) => ({
              "@type": "Question",
              "name": faq.q,
              "acceptedAnswer": { "@type": "Answer", "text": faq.a }
            }))
          }
        ]
      })}</script>
      <Navbar />
      <main>
        <section className="relative bg-gray-900 pt-24 pb-16 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Large%20pile%20of%20old%20computers%20laptops%20desktops%20and%20monitors%20stacked%20neatly%20in%20a%20professional%20electronics%20recycling%20facility%20in%20Los%20Angeles%2C%20bright%20clean%20warehouse%2C%20various%20computer%20brands%20and%20ages%20sorted%20for%20responsible%20recycling%2C%20eco-friendly%20green%20theme%2C%20industrial%20recycling%20center&width=1400&height=600&seq=computerhero001&orientation=landscape"
              alt="Computer recycling Los Angeles — desktops laptops and monitors for responsible recycling"
              className="w-full h-full object-cover object-center opacity-35"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-gray-900 via-gray-900/85 to-transparent"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: 'Home', path: '/' }, { label: 'Computer Recycling Los Angeles' }]} />
            <div className="max-w-2xl mt-8">
              <div className="inline-flex items-center gap-2 bg-[#1B6B3A] text-white text-xs font-bold px-3 py-1.5 rounded-full mb-4">
                <i className="ri-computer-line"></i>Free Computer Recycling
              </div>
              <h1 className="text-3xl md:text-5xl font-extrabold text-white leading-tight mb-5">
                Computer Recycling<span className="block text-[#74C69D]">in Los Angeles</span>
              </h1>
              <p className="text-gray-300 text-base md:text-lg leading-relaxed mb-6">
                Los Angeles' certified computer recycling service — <strong className="text-white">desktops, laptops, Macs, servers, and monitors</strong> accepted. Free data destruction included. Two convenient LA drop-off locations.
              </p>
              <div className="flex flex-wrap gap-3 mb-6">
                <a href="tel:+12136001000" className="inline-flex items-center gap-2 bg-[#1B6B3A] hover:bg-[#2D8653] text-white font-bold px-6 py-3 rounded-md transition-colors cursor-pointer whitespace-nowrap text-sm">
                  <i className="ri-phone-fill"></i>(213) 600-1000
                </a>
                <Link to="/contact" className="inline-flex items-center gap-2 bg-white/10 border border-white/30 text-white font-semibold px-6 py-3 rounded-md hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap text-sm">
                  <i className="ri-calendar-check-line"></i>Schedule Pickup
                </Link>
              </div>
              <div className="flex flex-wrap gap-5">
                {['All Brands Accepted', 'Free Data Wipe', 'No Appointment Needed', 'CalRecycle Certified'].map((t) => (
                  <div key={t} className="flex items-center gap-1.5 text-green-300 text-xs font-semibold">
                    <i className="ri-check-line text-[#74C69D]"></i>{t}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-[#1B6B3A] py-4">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10 text-white text-xs md:text-sm">
              {[
                { icon: 'ri-map-pin-line', text: '2 LA Drop-Off Locations' },
                { icon: 'ri-computer-line', text: 'All Computer Types' },
                { icon: 'ri-shield-check-line', text: 'Free Data Destruction' },
                { icon: 'ri-award-line', text: 'CalRecycle CEWD-127531' },
              ].map((i) => (
                <div key={i.text} className="flex items-center gap-2">
                  <i className={`${i.icon} text-[#74C69D]`}></i>
                  <span className="font-semibold">{i.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 bg-[#F4FAF7]">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">What We Accept</div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">All Computer Types Accepted for <strong>Recycling in LA</strong></h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {computerTypes.map((t) => (
                <div key={t.label} className="bg-white rounded-xl p-6 flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-[#E8F5EE] rounded-xl shrink-0">
                    <i className={`${t.icon} text-[#1B6B3A] text-xl`}></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-sm mb-1">{t.label}</h3>
                    <p className="text-gray-500 text-xs leading-relaxed">{t.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">Why Greenly</div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">The Best Computer Recycling in Los Angeles</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {benefits.map((b) => (
                <div key={b.title} className="bg-[#F4FAF7] rounded-xl p-6">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#1B6B3A] rounded-xl mb-4">
                    <i className={`${b.icon} text-white text-lg`}></i>
                  </div>
                  <h3 className="font-bold text-gray-900 text-sm mb-2">{b.title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 bg-[#F4FAF7]">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <div className="text-[#1B6B3A] text-xs font-bold uppercase tracking-widest mb-2">Drop-Off Locations</div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">2 Computer Recycling Drop-Off Locations in Los Angeles</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white border border-gray-200 rounded-2xl p-6">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#E8F5EE] rounded-xl shrink-0">
                    <i className="ri-map-pin-2-fill text-[#1B6B3A] text-lg"></i>
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">Location 1 — West 3rd Street</div>
                    <div className="text-gray-600 text-sm mt-1">2542 W. 3rd Street, Los Angeles, CA 90057</div>
                    <div className="text-gray-400 text-xs mt-1">Mon–Fri 8am–5pm · Sat 9am–2pm</div>
                  </div>
                </div>
                <a href="https://maps.app.goo.gl/1GFb4ZnoPCREHhzy6" target="_blank" rel="nofollow noreferrer" className="inline-flex items-center gap-2 text-[#1B6B3A] font-semibold text-sm hover:underline cursor-pointer">
                  <i className="ri-direction-line"></i>Get Directions
                </a>
              </div>
              <div className="bg-white border border-gray-200 rounded-2xl p-6">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#E8F5EE] rounded-xl shrink-0">
                    <i className="ri-map-pin-2-fill text-[#1B6B3A] text-lg"></i>
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">Location 2 — West Slauson Ave</div>
                    <div className="text-gray-600 text-sm mt-1">4902 W. Slauson Ave, Los Angeles, CA 90056</div>
                    <div className="text-gray-400 text-xs mt-1">Mon–Fri 8am–5pm · Sat 9am–2pm</div>
                  </div>
                </div>
                <a href="https://maps.google.com/?q=4902+W+Slauson+Ave+Los+Angeles+CA+90056" target="_blank" rel="nofollow noreferrer" className="inline-flex items-center gap-2 text-[#1B6B3A] font-semibold text-sm hover:underline cursor-pointer">
                  <i className="ri-direction-line"></i>Get Directions
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 bg-white">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900">Computer Recycling FAQs — Los Angeles</h2>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
                  <button className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                    <span className="text-sm font-bold text-gray-900">{faq.q}</span>
                    <div className="w-5 h-5 flex items-center justify-center shrink-0">
                      <i className={`text-[#1B6B3A] ${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'}`}></i>
                    </div>
                  </button>
                  {openFaq === i && <div className="px-5 pb-4"><p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p></div>}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 bg-[#F4FAF7]">
          <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-lg font-extrabold text-gray-900 mb-5">Also Recycle at the Same Drop-Off</h2>
            <div className="flex flex-wrap justify-center gap-4">
              {[
                { label: 'TV Recycling', path: '/tv-recycling', icon: 'ri-tv-line' },
                { label: 'Battery Recycling', path: '/battery-recycling', icon: 'ri-battery-line' },
                { label: 'Hard Drive Destruction', path: '/hard-drive-destruction', icon: 'ri-hard-drive-line' },
                { label: 'All Electronics', path: '/services', icon: 'ri-recycle-line' },
              ].map((s) => (
                <Link key={s.path} to={s.path} className="flex items-center gap-2 bg-white border border-gray-200 hover:border-[#1B6B3A] text-gray-700 hover:text-[#1B6B3A] px-5 py-3 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap">
                  <i className={s.icon}></i>{s.label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 bg-[#1B6B3A]">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-4">Ready to Recycle Your Computers in Los Angeles?</h2>
            <p className="text-green-100 text-sm md:text-base mb-8 max-w-xl mx-auto">Drop off your computers, laptops, and monitors at either LA location — free, no appointment needed. Free business pickup also available.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="tel:+12136001000" className="inline-flex items-center justify-center gap-2 bg-white text-[#1B6B3A] font-bold px-8 py-3 rounded-md hover:bg-green-50 transition-colors cursor-pointer whitespace-nowrap text-sm">
                <i className="ri-phone-fill"></i>Call (213) 600-1000
              </a>
              <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-white/20 border border-white/40 text-white font-semibold px-8 py-3 rounded-md hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap text-sm">
                <i className="ri-map-pin-line"></i>Get Directions
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default ComputerRecyclingPage;
